import moment from 'moment'
import React, { useCallback, useContext, useRef } from 'react'

import DatesDropdown from './DatesDropdown'
import DatesModal from './DatesModal'
import {
  isSingleDateSelectionItem,
  itemIsInItinerary,
  itemTypesWithNoEndDate,
  useItemOutsideOfTripDates,
} from './utils'
import BaseBookmarkToItineraryButton from '../Bookmark/Common/BaseBookmarkToItineraryButton'
import DateRangeDropdown from '../TripModal/Common/DateRangeDropdown'
import MobileSelectDatesSelfClose from '../TripModal/Common/MobileSelectDatesSelfClose'

import ModalBase from 'LuxKit/Modal/ModalBase'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import useToggle from 'hooks/useToggle'
import noop from 'lib/function/noop'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { AddToItineraryDatesRequiredModal } from 'tripPlanner/components/Itinerary/AddToItineraryDatesRequiredModal'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { CustomItem, ExperienceItem } from 'tripPlanner/types/tripItem'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import switchFunc from 'lib/function/switchFunc'
import { PatchTripItemRequest } from 'tripPlanner/api/tripItem/types'

const getCalendarStartEndLabels = switchFunc(
  {
    ACCOMMODATION: {
      startLabel: 'Check-in',
      endLabel: 'Check out',
    },
  },
  {},
)

function buildUpdatePayload(item: CustomItem | ExperienceItem, startDateMoment: moment.Moment, endDateMoment: moment.Moment): PatchTripItemRequest {
  const withEndDate = !itemTypesWithNoEndDate.includes(item.type as any)
  const startDate = startDateMoment.format(ISO_DATE_FORMAT)
  const endDate = withEndDate ? endDateMoment.format(ISO_DATE_FORMAT) : undefined

  if (item.type === 'EXPERIENCE') {
    const modifications: Extract<PatchTripItemRequest, { type: 'EXPERIENCE' }> = {
      type: item.type,
      startDate,
      endDate,
      // Clear times/tickets, since they depend on the date and we're changing it
      startTime: null,
      endTime: null,
      savedItemData: item.savedItemData ? {
        ...item.savedItemData,
        date: startDate,
        tickets: undefined,
      } : undefined,
    }
    return modifications
  }
  return {
    type: item.type,
    startDate,
    endDate,
  }
}

export interface Props {
  item: CustomItem | ExperienceItem
  tripId?: string
  variant: 'chip' | 'button'
  onDatesRequiredMessage?: () => void
  onRequestAddTripDates: (
    onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
  ) => void
  placement?: 'top' | 'bottom'
}

function ItineraryDateButton({
  item: tripItem,
  tripId: tripIdProp,
  variant,
  onDatesRequiredMessage = noop,
  onRequestAddTripDates,
  placement,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const { canEdit } = useTripPermissions(tripId)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const {
    value: singleDateDropdownOpen,
    toggle: toggleSingleDateDropdown,
    off: closeSingleDateDropdown,
  } = useToggle()
  const {
    value: dateRangeDropdownOpen,
    toggle: toggleDateRangeDropdown,
    off: closeDateRangeDropdown,
  } = useToggle()

  const isInItinerary = itemIsInItinerary(tripItem, trip)

  const { mutate: patchItem, isLoading: isUpdating } = usePatchTripItem()

  const addItemToItineraryWithDates = useCallback(
    async(startDate: moment.Moment, endDate: moment.Moment) => {
      if (!trip || !tripItem) {
        return
      }

      patchItem(
        {
          tripId: trip.id,
          tripItemId: tripItem.id,
          tripItem: buildUpdatePayload(tripItem, startDate, endDate),
        },
        {
          onSuccess: () => {
            showSnackbar('Added to itinerary', 'success')
          },
        },
      )
    },
    [patchItem, trip, tripItem],
  )

  const removeFromItinerary = useCallback(async() => {
    closeSingleDateDropdown()
    closeDateRangeDropdown()
    if (!trip || !tripItem) {
      return
    }
    const withEndDate = !itemTypesWithNoEndDate.includes(tripItem.type as any)

    patchItem(
      {
        tripId: trip.id,
        tripItemId: tripItem.id,
        tripItem: {
          type: tripItem.type,
          startDate: null,
          endDate: withEndDate ? null : undefined,
        },
      },
      {
        onSuccess: () => {
          showSnackbar('Removed from itinerary', 'success')
        },
      },
    )
  }, [
    closeDateRangeDropdown,
    closeSingleDateDropdown,
    trip,
    tripItem,
    patchItem,
  ])

  const onSelectSingleDate = useCallback(
    (date: moment.Moment) => {
      closeSingleDateDropdown()
      addItemToItineraryWithDates(date, date)
    },
    [addItemToItineraryWithDates, closeSingleDateDropdown],
  )

  const onSelectDateRange = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      closeDateRangeDropdown()
      addItemToItineraryWithDates(startDate, endDate ?? startDate)
    },
    [addItemToItineraryWithDates, closeDateRangeDropdown],
  )

  const onSelectDateRangeObj = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: moment.Moment
      endDate?: moment.Moment | null
    }) => {
      closeDateRangeDropdown()
      addItemToItineraryWithDates(startDate, endDate ?? startDate)
    },
    [addItemToItineraryWithDates, closeDateRangeDropdown],
  )

  const openDateSelector = useCallback(
    async(tripStartDate: moment.Moment, tripEndDate: moment.Moment) => {
      if (isSingleDateSelectionItem(tripItem)) {
        if (isMobile) {
          const selection = await showModal<moment.Moment | null | undefined>(
            <DatesModal
              selectedDate={isInItinerary ? tripItem.startDate : undefined}
              startDate={tripStartDate}
              endDate={tripEndDate}
            />,
          )
          if (selection === null) {
            removeFromItinerary()
          } else if (moment.isMoment(selection)) {
            onSelectSingleDate(selection)
          }
        } else {
          toggleSingleDateDropdown()
        }
      } else {
        if (isMobile) {
          showModal(
            <ModalBase height="full">
              <MobileSelectDatesSelfClose
                initialStartDate={tripItem.startDate}
                initialEndDate={tripItem.endDate}
                onChange={onSelectDateRange}
                onRemove={removeFromItinerary}
              />
            </ModalBase>,
          )
        } else {
          toggleDateRangeDropdown()
        }
      }
    },
    [
      isInItinerary,
      isMobile,
      onSelectDateRange,
      onSelectSingleDate,
      removeFromItinerary,
      showModal,
      toggleDateRangeDropdown,
      toggleSingleDateDropdown,
      tripItem,
    ],
  )

  const onButtonClick = useCallback(async() => {
    if (!trip) {
      return
    } else if (!trip.startDate || !trip.endDate) {
      onDatesRequiredMessage()
      const didRequestAddTripDates = await showModal<boolean>(
        <AddToItineraryDatesRequiredModal variation="exploreItem" />,
      )

      if (didRequestAddTripDates) {
        onRequestAddTripDates(openDateSelector)
      }
    } else {
      openDateSelector(trip.startDate, trip.endDate)
    }
  }, [
    trip,
    onDatesRequiredMessage,
    showModal,
    onRequestAddTripDates,
    openDateSelector,
  ])

  // check there isn't a date conflict.
  const isItemOutsideOfTripDates: boolean = useItemOutsideOfTripDates(tripItem)

  if (!canEdit || !tripId) {
    return null
  }

  const isAccommodation = tripItem.type === 'ACCOMMODATION'

  const { startLabel, endLabel } = getCalendarStartEndLabels(tripItem.type)

  return (
    <>
      <BaseBookmarkToItineraryButton
        itineraryDate={isInItinerary ? tripItem.startDate : undefined}
        onClick={onButtonClick}
        isProcessing={isUpdating}
        variant={variant}
        ref={triggerRef}
        hasWarning={isItemOutsideOfTripDates}
      />
      {singleDateDropdownOpen && trip?.startDate && trip.endDate && (
        <DatesDropdown
          isOpen
          onClose={closeSingleDateDropdown}
          onSelectDate={onSelectSingleDate}
          onSelectRemoveDate={removeFromItinerary}
          selectedDate={isInItinerary ? tripItem.startDate : undefined}
          minDate={trip.startDate}
          maxDate={trip.endDate}
          triggerRef={triggerRef}
          portaled
          placement={placement}
        />
      )}
      {!isSingleDateSelectionItem(tripItem) && dateRangeDropdownOpen && (
        <DateRangeDropdown
          startDate={tripItem.startDate}
          endDate={tripItem.endDate}
          initDate={trip?.startDate}
          triggerRef={triggerRef}
          isOpen
          onDatesChange={onSelectDateRangeObj}
          onClear={removeFromItinerary}
          onClose={closeDateRangeDropdown}
          rangeType={isAccommodation ? 'nights' : 'days'}
          portaled
          placement={placement}
          startLabel={startLabel}
          endLabel={endLabel}
        />
      )}
    </>
  )
}

export default ItineraryDateButton
